import React from 'react';
import { HardHat, Users, Shield, Briefcase, Target, AlertTriangle, CheckCircle, TrendingUp } from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ReferenceLine,
  PieChart, Pie, Cell
} from 'recharts';
import { CHART_TOOLTIP_STYLE } from '../../config/dashboardConstants';
import PillToggle from '../UI/PillToggle';

const DRCSection = ({
  drcData, drcLoading, drcViewMode, setDrcViewMode,
  drcRatio, drcRatioDisplay,
  lqsCount, migrantCount, foreignTotal, foreignShare, foreignShareDisplay,
  wpCount, spCount, epCount, localEmployeesCount,
  momLimit, maxForeignWorkers, bufferCapacity,
  ttmChartData, yoyChartData, ttmYAxisDomain, yoyYAxisDomain,
  trendRange, minDrc, maxDrc,
  yoyYears,
}) => {
  const [curYear, prevYear, prev2Year] = yoyYears || (() => { const y = new Date().getFullYear(); return [y, y - 1, y - 2]; })();
  const yCurKey = `y${curYear}`;
  const yPrevKey = `y${prevYear}`;
  const yPrev2Key = `y${prev2Year}`;

  const FULL_MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  let latestMonthIdx = -1;
  if (yoyChartData?.length) {
    for (let i = yoyChartData.length - 1; i >= 0; i--) {
      if (yoyChartData[i]?.[yCurKey] != null) { latestMonthIdx = i; break; }
    }
  }
  const latestMonthName = latestMonthIdx >= 0 ? FULL_MONTHS[latestMonthIdx] : '';
  const latestShortMonth = latestMonthIdx >= 0 ? yoyChartData[latestMonthIdx]?.month : '';
  const latestPoint = latestMonthIdx >= 0 ? yoyChartData[latestMonthIdx] : null;
  const yoyCurVal = latestPoint?.[yCurKey] ?? Number(drcRatioDisplay);
  const yoyPrevVal = latestPoint?.[yPrevKey];
  const yoyPrev2Val = latestPoint?.[yPrev2Key];
  const prevChangePct = yoyPrevVal ? (((yoyCurVal - yoyPrevVal) / yoyPrevVal) * 100).toFixed(1) : null;
  const prev2ChangePct = yoyPrev2Val ? (((yoyCurVal - yoyPrev2Val) / yoyPrev2Val) * 100).toFixed(1) : null;

  return (
      <section className="space-y-6" aria-label="Foreign Worker Compliance Dashboard">
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          {/* Section Header - Following Design System Typography */}
          <div className="space-y-1.5 sm:space-y-2 mb-4 sm:mb-6">
            <div className="flex items-center gap-2 min-w-0">
              <HardHat className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 flex-shrink-0" />
              <h2 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">Foreign Worker Quota</h2>
            </div>
            <p className="text-xs sm:text-sm text-gray-600">
              Construction Sector • MOM Limit: 83.3% (1:5 ratio)
              {drcData?.data_freshness === 'error' && (
                <span className="ml-2 text-red-600 font-medium">⚠️ Authentication required</span>
              )}
            </p>
          </div>

          {/* 4 Key Metrics Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            
            {/* 1. Current DRC Ratio - Following Design System Metrics Card Pattern */}
            {/* NOTE: Spans 2 cols on sm+ because it contains an extra horizontal bar visualisation */}
            <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6 sm:col-span-2 lg:col-span-1" title={`DRC = Migrant (WP+S) ÷ LQS (SC+PR). ${migrantCount} ÷ ${lqsCount} = ${drcRatioDisplay}. Foreign share ${foreignShareDisplay}%`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center min-w-0">
                  <div className="w-9 h-9 sm:w-12 sm:h-12 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Target className="w-4 h-4 sm:w-6 sm:h-6 text-yellow-600" />
                  </div>
                  <div className="ml-2 sm:ml-4 min-w-0">
                    <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">1 : {Number(drcRatioDisplay)}</p>
                    <p className="text-xs sm:text-sm text-gray-600 truncate">Current DRC</p>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-lg sm:text-2xl font-bold text-green-600">{foreignShareDisplay}%</p>
                  <p className="text-xs sm:text-sm text-gray-600">&nbsp;</p>
                </div>
              </div>
              
              <div className="mt-4">
                <div className="relative">
                  {/* Horizontal Bar */}
                  <div className="w-full h-3 bg-gradient-to-r from-emerald-500 via-yellow-500 to-red-500 rounded-full">
                    {/* Current Position Indicator */}
                    <div 
                      className="absolute top-0 w-4 h-4 bg-yellow-600 rounded-full border-2 border-white shadow-lg transform -translate-y-0.5"
                      style={{
                        left: `${Math.min(100, Math.max(0, (((drcRatio || 0) - 3) / (5 - 3) * 100)))}%`
                      }}
                    ></div>
                  </div>
                  
                  {/* Labels */}
                  <div className="flex justify-between mt-2 text-xs">
                    <div className="text-center">
                      <div className="font-semibold text-emerald-600">1:3</div>
                      <div className="text-gray-500">Company Target</div>
                    </div>
                    <div className="text-center">
                      <div className="font-semibold text-red-600">1:5</div>
                      <div className="text-gray-500">MOM Compliance</div>
                    </div>
                  </div>
                  

                </div>
              </div>
              

            </div>

            {/* 2. Local Employees - Following Design System Metrics Card Pattern */}
            <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6" title={`LQS locals (SC+PR): ${lqsCount}. Effective LQS used when provided (avg over 3 months).`}> 
              <div className="flex items-start">
                <div className="w-9 h-9 sm:w-12 sm:h-12 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Users className="w-4 h-4 sm:w-6 sm:h-6 text-emerald-600" />
                </div>
                <div className="ml-2 sm:ml-4 min-w-0">
                  <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
                    {drcData?.workforce_breakdown?.local_employees || 0}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600 truncate">Local Employees</p>
                  <div className="mt-1 sm:mt-2 space-y-0.5 sm:space-y-1">
                    <p className="text-[11px] sm:text-xs text-gray-500 truncate">
                      LQS: {drcData?.lqs_metrics?.effective_local_count || 0}
                    </p>
                    <p className="text-[11px] sm:text-xs text-emerald-600 font-medium truncate">
                      {drcData?.workforce_breakdown?.citizen_count || 0} SC, {drcData?.workforce_breakdown?.pr_count || 0} PR
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* 3. S Pass Available - Following Design System Metrics Card Pattern */}
            <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6" title={`S Pass holders (included in DRC): ${spCount} / Limit: ${Number(drcData?.quota_limits?.s_pass_limit ?? 0)} • Available slots follow sector cap`}> 
              <div className="flex items-start">
                <div className="w-9 h-9 sm:w-12 sm:h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Shield className="w-4 h-4 sm:w-6 sm:h-6 text-purple-600" />
                </div>
                <div className="ml-2 sm:ml-4 min-w-0">
                  <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
                    {drcData?.quota_limits?.available_s_pass_slots || 0}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600 truncate">Available S Pass</p>
                  <div className="mt-1 sm:mt-2 space-y-0.5 sm:space-y-1">
                    <p className="text-[11px] sm:text-xs text-gray-500 truncate">
                      Current: {drcData?.workforce_breakdown?.s_pass_holders || 0}
                    </p>
                    <p className="text-[11px] sm:text-xs text-gray-500 truncate">
                      Limit: {drcData?.quota_limits?.s_pass_limit || 0}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* 4. Work Permit Available - Following Design System Metrics Card Pattern */}
            <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6" title={`Work Permit holders (included in DRC): ${wpCount} / Quota: ${Number(drcData?.quota_limits?.work_permit_quota ?? 0)} • Available slots based on current LQS and sector cap`}> 
              <div className="flex items-start">
                <div className="w-9 h-9 sm:w-12 sm:h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Briefcase className="w-4 h-4 sm:w-6 sm:h-6 text-orange-600" />
                </div>
                <div className="ml-2 sm:ml-4 min-w-0">
                  <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
                    {drcData?.quota_limits?.available_wp_slots || 0}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600 truncate">Available Work Permit</p>
                  <div className="mt-1 sm:mt-2 space-y-0.5 sm:space-y-1">
                    <p className="text-[11px] sm:text-xs text-gray-500 truncate">
                      Current: {drcData?.workforce_breakdown?.work_permit_holders || 0}
                    </p>
                    <p className="text-[11px] sm:text-xs text-gray-500 truncate">
                      Limit: {drcData?.quota_limits?.work_permit_quota || 0}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* No employee data message */}
          {drcData && (drcData.workforce_breakdown?.local_employees === 0 && drcData.workforce_breakdown?.foreign_workers === 0) && (
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center">
                <Users className="w-5 h-5 text-blue-600 mr-2" />
                <div>
                  <p className="text-sm font-medium text-blue-800">
                    No Employee Data Available
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    Foreign Worker Quota calculations require employee data. Please add employees to the system to see actual quota metrics.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Authentication prompt */}
          {drcData?.data_freshness === 'error' && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
                <div>
                  <p className="text-sm font-medium text-red-800">
                    Authentication Required
                  </p>
                  <p className="text-xs text-red-600 mt-1">
                    Please log in to view live Foreign Worker Compliance data. The system will automatically use your credentials.
                  </p>
                </div>
              </div>
            </div>
          )}


          {/* DRC Ratio Trend Chart */}
          <div className="mt-6 sm:mt-8 border-t pt-6 sm:pt-8">
            <div className="mb-4 sm:mb-6">
              <div className="flex items-center gap-2 sm:gap-3 justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 flex-shrink-0" />
                  <h3 className="text-lg sm:text-2xl font-bold text-gray-900 min-w-0 truncate">DRC Ratio Trends</h3>
                </div>
                <PillToggle
                  value={drcViewMode}
                  onChange={setDrcViewMode}
                  options={[
                    { id: 'ttm', label: 'TTM', title: 'Trailing 12-Month View' },
                    { id: 'yoy', label: 'YoY', title: 'Year-on-Year View' },
                  ]}
                  variant="compact"
                  theme="admin"
                  className="flex-shrink-0"
                  ariaLabel="DRC view mode"
                />
              </div>
              <p className="text-xs sm:text-sm text-gray-600 mt-1 ml-7 sm:ml-8">
                Critical compliance monitoring for foreign worker dependency
              </p>
              <p className="text-[11px] sm:text-xs text-gray-500 mt-0.5 ml-7 sm:ml-8">
                *End-of-month snapshots
              </p>
            </div>
            </div>

            <div className="bg-white rounded-lg border p-4 sm:p-6">
              {/* TTM View */}
              {drcViewMode === 'ttm' && (
              <div className="block">
                {/* Chart Header */}
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between mb-4">
                  <div className="min-w-0">
                    <h4 className="text-base sm:text-lg font-semibold text-gray-900 truncate">Trailing 12-Month DRC Ratio</h4>
                    <p className="text-xs sm:text-sm text-gray-600">Rolling 12-month periods • Current: <span className="font-semibold text-blue-600">{Number(drcRatioDisplay)}</span></p>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 sm:gap-4">
                    <div className="flex items-center gap-1.5 sm:gap-2">
                      <svg width="20" height="2" className="flex-shrink-0">
                        <line
                          x1="0"
                          y1="1"
                          x2="20"
                          y2="1"
                          stroke="#F59E0B"
                          strokeWidth="2"
                          strokeDasharray="6 6"
                        />
                      </svg>
                      <span className="text-[11px] sm:text-xs text-gray-600">MOM Limit (5.0)</span>
                    </div>
                    <div className="flex items-center gap-1.5 sm:gap-2">
                      <svg width="20" height="2" className="flex-shrink-0">
                        <line
                          x1="0"
                          y1="1"
                          x2="20"
                          y2="1"
                          stroke="#1E3A8A"
                          strokeWidth="2"
                          strokeDasharray="4 4"
                        />
                      </svg>
                      <span className="text-[11px] sm:text-xs text-gray-600">Company Target (3.0)</span>
                    </div>
                    <div className="flex items-center gap-1.5 sm:gap-2">
                      <svg width="20" height="2" className="flex-shrink-0">
                        <line
                          x1="0"
                          y1="1"
                          x2="20"
                          y2="1"
                          stroke="#EF4444"
                          strokeWidth="2"
                          strokeDasharray="0"
                        />
                      </svg>
                      <span className="text-[11px] sm:text-xs text-gray-600">Current Trend</span>
                    </div>
                  </div>
                </div>

                {/* DRC Line Chart (Recharts) */}
                <div className="relative">
                  <div className="h-80">
                    {drcLoading ? (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="animate-pulse w-11/12 h-4 bg-gray-200 rounded mb-4"></div>
                      </div>
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={ttmChartData} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                          <YAxis domain={ttmYAxisDomain} tick={{ fontSize: 11 }} />
                          <Tooltip formatter={(v) => [`${v}`, 'DRC']} />
                          <Legend 
                            wrapperStyle={{ display: 'none' }}
                            content={null}
                          />
                          <ReferenceLine y={5} stroke="#F59E0B" strokeDasharray="6 6" label={{ value: 'MOM Limit (5.0)', position: 'insideTopRight', fontSize: 10, fill: '#9A3412' }} />
                          <ReferenceLine y={3} stroke="#1E3A8A" strokeDasharray="4 4" label={{ value: 'Company Target (3.0)', position: 'insideTopRight', fontSize: 10, fill: '#1E3A8A' }} />
                          <Line type="monotone" dataKey="drc" name="" stroke="#EF4444" dot={{ r: 3 }} activeDot={{ r: 5 }} strokeWidth={2} />
                        </LineChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>

                {/* TTM Insights */}
                <div className="mt-4 sm:mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                      <h5 className="font-semibold text-gray-900">Current Status</h5>
                    </div>
                    <p className="text-sm text-gray-700 mt-1">
                      DRC {drcLoading ? '...' : drcRatioDisplay} - Well below MOM limit (5.0)
                      <br />
                      <span className="font-medium">Safe compliance zone</span>
                    </p>
                  </div>
                  
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <TrendingUp className="w-4 h-4 text-blue-600 mr-2" />
                      <h5 className="font-semibold text-gray-900">12-Month Trend</h5>
                    </div>
                    <p className="text-sm text-gray-700 mt-1">
                      Fluctuated between {trendRange}
                      <br />
                      <span className="font-medium">Stable within safe range</span>
                    </p>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <Target className="w-4 h-4 text-orange-600 mr-2" />
                      <h5 className="font-semibold text-gray-900">Buffer Capacity</h5>
                    </div>
                    <p className="text-sm text-gray-700 mt-1">
                      Can hire {bufferCapacity} more foreign workers
                      <br />
                      <span className="font-medium">Before reaching MOM limit</span>
                    </p>
                  </div>
                </div>

                {/* Quota Utilization Donuts — S Pass & Work Permit */}
                {(() => {
                  const wpQuota = Number(drcData?.quota_limits?.work_permit_quota ?? 0);
                  const wpUsed = wpCount;
                  const wpAvail = Number(drcData?.quota_limits?.available_wp_slots ?? Math.max(0, wpQuota - wpUsed));
                  const spLimit = Number(drcData?.quota_limits?.s_pass_limit ?? drcData?.s_pass_metrics?.limit_count ?? 0);
                  const spUsed = spCount;
                  const spAvail = Number(drcData?.quota_limits?.available_s_pass_slots ?? drcData?.s_pass_metrics?.available_slots ?? Math.max(0, spLimit - spUsed));

                  const hasQuotaData = wpQuota > 0 || spLimit > 0;
                  if (!hasQuotaData) return null;

                  const buildDonutData = (used, available) => [
                    { name: 'Used', value: used },
                    { name: 'Available', value: available },
                  ];

                  const renderDonut = (title, used, total, available, usedColor, availColor) => {
                    const pct = total > 0 ? ((used / total) * 100).toFixed(0) : '0';
                    const data = buildDonutData(used, available);
                    const isHigh = total > 0 && (used / total) > 0.85;
                    return (
                      <div className="bg-white border border-gray-200 rounded-lg p-4 flex flex-col items-center">
                        <h5 className="font-semibold text-gray-900 text-sm mb-3">{title}</h5>
                        <div className="w-32 h-32 relative">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={data}
                                innerRadius={36}
                                outerRadius={54}
                                dataKey="value"
                                startAngle={90}
                                endAngle={-270}
                                paddingAngle={2}
                                stroke="none"
                              >
                                <Cell fill={usedColor} />
                                <Cell fill={availColor} />
                              </Pie>
                              <Tooltip
                                contentStyle={CHART_TOOLTIP_STYLE}
                                formatter={(value, name) => [`${value}`, name]}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                          <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <span className={`text-lg font-bold ${isHigh ? 'text-red-600' : 'text-gray-900'}`}>{pct}%</span>
                            <span className="text-[10px] text-gray-500">used</span>
                          </div>
                        </div>
                        <div className="mt-2 text-center space-y-0.5">
                          <p className="text-xs text-gray-700">
                            <span className="font-semibold">{used}</span> / {total} slots used
                          </p>
                          <p className={`text-xs font-medium ${isHigh ? 'text-red-600' : 'text-green-600'}`}>
                            {available} {available === 1 ? 'slot' : 'slots'} remaining
                          </p>
                        </div>
                      </div>
                    );
                  };

                  return (
                    <div className="mt-6">
                      <h5 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <Target className="w-4 h-4 text-indigo-600" aria-hidden="true" />
                        Quota Slot Utilization
                      </h5>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {spLimit > 0 && renderDonut('S Pass Slots', spUsed, spLimit, spAvail, '#6366F1', '#E0E7FF')}
                        {wpQuota > 0 && renderDonut('Work Permit Slots', wpUsed, wpQuota, wpAvail, '#F59E0B', '#FEF3C7')}
                      </div>
                    </div>
                  );
                })()}
              </div>
              )}

              {/* YoY View */}
              {drcViewMode === 'yoy' && (
              <div className="block">
                {/* Chart Header */}
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between mb-4">
                  <div className="min-w-0">
                    <h4 className="text-base sm:text-lg font-semibold text-gray-900 truncate">Year-on-Year DRC Comparison</h4>
                    <p className="text-xs sm:text-sm text-gray-600">Same month across multiple years • {latestShortMonth} {curYear}: <span className="font-semibold text-blue-600">{Number(drcRatioDisplay)}</span></p>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 sm:gap-4">
                    <div className="flex items-center gap-1.5 sm:gap-2">
                      <div className="w-3 h-0.5 bg-red-500"></div>
                      <span className="text-[11px] sm:text-xs text-gray-600">{curYear} (Current)</span>
                    </div>
                    <div className="flex items-center gap-1.5 sm:gap-2">
                      <div className="w-3 h-0.5 bg-green-500"></div>
                      <span className="text-[11px] sm:text-xs text-gray-600">{prevYear}</span>
                    </div>
                    <div className="flex items-center gap-1.5 sm:gap-2">
                      <div className="w-3 h-0.5 bg-purple-500"></div>
                      <span className="text-[11px] sm:text-xs text-gray-600">{prev2Year}</span>
                    </div>
                  </div>
                </div>

                {/* YoY Line Chart (Recharts) */}
                <div className="relative">
                  <div className="h-80">
                    {drcLoading ? (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="animate-pulse w-11/12 h-4 bg-gray-200 rounded mb-4"></div>
                      </div>
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={yoyChartData} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                          <YAxis domain={yoyYAxisDomain} tick={{ fontSize: 11 }} />
                          <Tooltip />
                          <Line type="monotone" dataKey={yCurKey} name={`${curYear} (Current)`} stroke="#EF4444" strokeWidth={2} strokeDasharray="4 4" dot={{ r: 3 }} />
                          <Line type="monotone" dataKey={yPrevKey} name={`${prevYear}`} stroke="#10B981" strokeWidth={1.5} dot={false} />
                          <Line type="monotone" dataKey={yPrev2Key} name={`${prev2Year}`} stroke="#8B5CF6" strokeWidth={1.5} dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>

                {/* YoY Cards Side by Side */}
                <div className="mt-4 sm:mt-6 grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-6">
                  {/* YoY Comparison Card */}
                  <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6">
                    <div className="mb-4 sm:mb-6">
                      <h5 className="text-base sm:text-lg font-semibold text-blue-900">Year-on-Year DRC Comparison</h5>
                      <p className="text-xs sm:text-sm text-blue-600">Latest complete month vs historical same-month periods</p>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-2 sm:gap-4">
                      <div className="text-center">
                        <div className="flex items-center justify-center mb-1 sm:mb-2">
                          <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-blue-600 rounded-full mr-1.5 sm:mr-2"></div>
                          <h6 className="text-xs sm:text-sm font-semibold text-blue-800">{latestMonthName} {curYear}</h6>
                        </div>
                        <div className="text-xl sm:text-2xl font-bold text-blue-600 mb-0.5 sm:mb-1">{yoyCurVal}</div>
                        <p className="text-[10px] sm:text-xs text-blue-700">Latest DRC ratio</p>
                      </div>
                      
                      <div className="text-center">
                        <div className="flex items-center justify-center mb-1 sm:mb-2">
                          <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-green-500 rounded-full mr-1.5 sm:mr-2"></div>
                          <h6 className="text-xs sm:text-sm font-semibold text-green-800">{latestMonthName} {prevYear}</h6>
                        </div>
                        <div className="text-xl sm:text-2xl font-bold text-green-600 mb-0.5 sm:mb-1">{yoyPrevVal ?? 'N/A'}</div>
                        <p className="text-[10px] sm:text-xs text-green-700">
                          {prevChangePct !== null ? (
                            <span className="font-medium text-green-800">{Number(prevChangePct) >= 0 ? '+' : ''}{prevChangePct}%</span>
                          ) : '—'} vs {curYear}
                        </p>
                      </div>

                      <div className="text-center">
                        <div className="flex items-center justify-center mb-1 sm:mb-2">
                          <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-purple-500 rounded-full mr-1.5 sm:mr-2"></div>
                          <h6 className="text-xs sm:text-sm font-semibold text-purple-800">{latestMonthName} {prev2Year}</h6>
                        </div>
                        <div className="text-xl sm:text-2xl font-bold text-purple-600 mb-0.5 sm:mb-1">{yoyPrev2Val ?? 'N/A'}</div>
                        <p className="text-[10px] sm:text-xs text-purple-700">
                          {prev2ChangePct !== null ? (
                            <span className="font-medium text-purple-800">{Number(prev2ChangePct) >= 0 ? '+' : ''}{prev2ChangePct}%</span>
                          ) : '—'} vs {curYear}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* YoY Insights */}
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 sm:p-6">
                    <div className="flex items-start">
                      <TrendingUp className="w-5 h-5 text-gray-600 mr-3 mt-1 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-3">Year-on-Year Analysis</h4>
                        <div className="space-y-2 text-sm text-gray-600">
                          <p>
                            <strong>3-Year Trend:</strong>{' '}
                            {yoyPrev2Val != null ? (
                              <>DRC moved from {yoyPrev2Val} ({prev2Year}) to {yoyCurVal} ({curYear}) — {prev2ChangePct !== null ? `${Number(prev2ChangePct) >= 0 ? '+' : ''}${prev2ChangePct}%` : 'N/A'} over 2 years.</>
                            ) : (
                              <>Current DRC is {drcRatioDisplay} ({curYear}). Insufficient historical data for {prev2Year}.</>
                            )}
                          </p>
                          <p>
                            <strong>Year-over-Year:</strong>{' '}
                            {yoyPrevVal != null ? (
                              <>{curYear} vs {prevYear}: {Number(prevChangePct) >= 0 ? 'increased' : 'decreased'} by {Math.abs(Number(prevChangePct))}% at the same point in the year.</>
                            ) : (
                              <>No {prevYear} data available for same-month comparison.</>
                            )}
                          </p>
                          <p>
                            <strong>Strategic Insight:</strong> {Number(drcRatioDisplay) < 3.5
                              ? 'Current ratio is within safe compliance margins relative to the MOM limit (5.0).'
                              : Number(drcRatioDisplay) < 4.5
                                ? 'Ratio approaching caution zone — monitor foreign worker dependency closely.'
                                : 'Ratio is near the MOM regulatory limit — immediate review recommended.'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              )}

            </div>
          </div>
      </section>
  );
};

export default DRCSection;
